// Step 3 Email insured (spec C8 Path A step 3, C9 Phase 1): the template the portal suggests, filled
// from AMS360 and the dec page, editable for this send only; Open in Outlook fills a new message in
// the VA's own Outlook, the files are downloaded to drag in, and "Mark as sent" keeps the email on
// the record. Sending from the portal comes in Phase 2. Markup from design/kit (StepCard, Chip,
// Input, Button); the kit has no email card yet, so this one is built from those parts.
import { useEffect, useRef, useState } from 'react';
import type { Api, EmailDraft, EmailPayment, RenewalDetail, StepView } from '../auth/api';
import { Dialog } from '../shell/Dialog';
import { Toast } from '../shell/Toast';
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  FileIcon,
  ExternalLinkIcon,
  MailIcon,
} from '../shell/icons';
import { FoldChevron } from './StepFold';
import { useStepFold } from './use-step-fold';
import {
  fillInLine,
  longBodyLine,
  longMailto,
  mailtoHref,
  nextEmailText,
  outlookFallback,
  outlookWebHref,
  paymentChoices,
  sentStamp,
} from './email-lines';
import { stepStamp } from './step-lines';

type Props = {
  api: Api;
  renewal: RenewalDetail;
  /** The renewal dec page is attached: the step opens. */
  ready: boolean;
  /**
   * The renewal is closed: read only until it is reopened (spec C16), so the step stays folded with
   * its stamp and no email is prepared, opened or marked sent.
   */
  closed?: boolean;
  /** What the draft is built from (`draftRevision`): a change asks for the draft again. */
  revision?: string;
  /** A draft already in hand (tests); otherwise the step asks for it when it opens. */
  initial?: EmailDraft;
  /** Step 3 on the Steps card: done when ticked (Mark as sent ticks it once step 2 is done). */
  step?: StepView;
  /** The header line while the step is locked. */
  lockedText?: string;
  /** Told after Mark as sent, so the Steps card asks for the steps again. */
  onSent?: () => void;
  /** Ticks the step for an email marked sent before the step opened. */
  onMarkDone?: () => void;
  /** Skip email (S17 extras): ticks the step with the reason; true once saved. */
  onSkip?: (reason: string) => Promise<boolean>;
  /** Unticks the step, when it is the last step done and this person may. */
  undo?: (() => void) | null;
  /** The step's number, title and line: a branch's email step is step 2 (S17). */
  number?: number;
  title?: string;
  line?: string;
};

export function EmailStep({
  api,
  renewal,
  ready,
  closed = false,
  revision,
  initial,
  step,
  lockedText = 'Opens when the renewal dec page is attached',
  onSent,
  onMarkDone,
  onSkip,
  undo,
  number = 3,
  title = 'Email insured',
  line = 'Send the renewal from your Outlook.',
}: Props) {
  const [choice, setChoice] = useState<{ template?: string; payment?: EmailPayment }>({});
  const [draft, setDraft] = useState<EmailDraft | null>(initial ?? null);
  const [edit, setEdit] = useState({
    to: initial?.to ?? '',
    cc: initial?.cc ?? '',
    subject: initial?.subject ?? '',
    body: initial?.body ?? '',
  });
  const [failed, setFailed] = useState(false);
  const [opened, setOpened] = useState(false);
  // A done step folds away; Show the email opens it again (to send another, such as Wrong bank).
  const [unfolded, setUnfolded] = useState(false);
  const [marking, setMarking] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const [skipping, setSkipping] = useState(false);

  // The draft the text was last filled from, and the choice it was asked with.
  const shown = useRef<{ draft: EmailDraft | null; choice: typeof choice }>({
    draft: initial ?? null,
    choice,
  });

  useEffect(() => {
    if (!ready || closed) return;
    let live = true;
    api
      .emailDraft(renewal.id, choice)
      .then((d) => {
        if (!live) return;
        const last = shown.current;
        shown.current = { draft: d, choice };
        setDraft(d);
        // A new template or payment block starts a fresh email; edits are for one send only. A
        // newer draft (the reading finished, other files) refreshes the text unless it was edited.
        setEdit((e) => nextEmailText(e, last.draft, d, last.choice !== choice));
        setFailed(false);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [api, renewal.id, ready, closed, choice, revision]);

  const sent = draft?.sent[0] ?? null;
  const say = (text: string) => setToast({ id: Date.now(), text });

  async function copy(what: 'subject' | 'body') {
    try {
      await navigator.clipboard.writeText(edit[what]);
      say(what === 'subject' ? 'Subject copied.' : 'Body copied.');
    } catch {
      say('Could not copy. Select the text and copy it by hand.');
    }
  }

  function downloadAll() {
    for (const a of draft?.attachments ?? []) {
      const link = document.createElement('a');
      link.href = api.documentUrl(a.id, true);
      link.download = a.fileName;
      document.body.append(link);
      link.click();
      link.remove();
    }
  }

  async function markSent() {
    if (!draft) return;
    setMarking(true);
    const res = await api.markEmailSent(renewal.id, { templateKey: draft.template, ...edit });
    setMarking(false);
    if (res.ok) {
      setDraft({ ...draft, sent: res.body.sent });
      say('Email marked as sent.');
      onSent?.();
    } else say('Could not mark it sent. Try again.');
  }

  const href = draft ? mailtoHref(edit) : '';
  const fillIn = draft ? fillInLine(draft.missing, draft.fillIns) : null;

  const done = step ? step.state === 'done' : !!sent;
  const stamp = step?.stamp ? stepStamp(step) : sent ? sentStamp(sent) : null;
  // Done folds to one line; the current step is open (StepCard).
  const fold = useStepFold(done ? 'done' : ready ? 'now' : 'locked', !done);
  return (
    <div
      className={done ? 'step' : ready ? 'step now' : 'step locked'}
      aria-disabled={!ready}
      aria-label={`Step ${number}, ${title}`}
    >
      <div className="step-h" onClick={ready && !closed ? fold.toggle : undefined}>
        <span className={done ? 'num num-done' : ready ? 'num num-now' : 'num num-next'}>
          {done ? <CheckIcon className="ico s12" /> : number}
        </span>
        <span className="st-t">
          <span className="st-tr">
            <span className={done ? 'st-title' : ready ? 'st-title now' : 'st-title next'}>
              {title}
            </span>
          </span>
          <span className="st-desc t12 muted">
            {done && step?.details.skipped === true && typeof step.details.reason === 'string'
              ? `Skipped, no email sent. Reason: ${step.details.reason}`
              : ready
                ? line
                : lockedText}
          </span>
        </span>
        {done && stamp && <span className="stamp ok">{stamp}</span>}
        {ready && !closed && <FoldChevron open={fold.open} onToggle={fold.toggle} title={title} />}
      </div>
      {ready && !closed && fold.open && done && step && !unfolded && (
        <div className="step-b">
          <div className="row g8 wrap">
            <button type="button" className="linkbtn" onClick={() => setUnfolded(true)}>
              Show the email
            </button>
            {step?.state === 'done' && undo && (
              <button type="button" className="linkbtn ml-auto" onClick={undo}>
                Untick this step
              </button>
            )}
          </div>
        </div>
      )}
      {ready && !closed && fold.open && (!done || !step || unfolded) && (
        <div className="step-b">
          {failed && (
            <div className="fsx amber">
              <div className="fsx-h">
                <AlertTriangleIcon />
                <span>The email could not be prepared. Reload the page to try again.</span>
              </div>
            </div>
          )}
          {!draft && !failed && (
            <span className="readline">
              <span className="spin" />
              Preparing the email
            </span>
          )}
          {draft && (
            <>
              <div role="group" aria-labelledby="email-template">
                <div id="email-template" className="flab">
                  Template
                </div>
                <div className="fopts">
                  {draft.templates.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={t.key === draft.template ? 'chip chip-sm on' : 'chip chip-sm'}
                      aria-pressed={t.key === draft.template}
                      title={t.usedFor}
                      onClick={() => setChoice((c) => ({ ...c, template: t.key }))}
                    >
                      {t.name}
                      {t.key === draft.suggested && t.key !== draft.template && ' (suggested)'}
                    </button>
                  ))}
                </div>
              </div>
              {draft.showsPayment && (
                <div role="group" aria-labelledby="email-payment">
                  <div id="email-payment" className="flab">
                    Payment
                  </div>
                  <div className="fopts">
                    {paymentChoices.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className={p.key === draft.payment ? 'chip chip-sm on' : 'chip chip-sm'}
                        aria-pressed={p.key === draft.payment}
                        onClick={() => setChoice((c) => ({ ...c, payment: p.key }))}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {fillIn && (
                <div className="fsx amber">
                  <div className="fsx-h">
                    <AlertTriangleIcon />
                    <span>{fillIn}</span>
                  </div>
                </div>
              )}
              <label className="col g4">
                <span className="flab">To</span>
                <input
                  className="input"
                  value={edit.to}
                  onChange={(e) => setEdit({ ...edit, to: e.target.value })}
                />
              </label>
              <label className="col g4">
                <span className="flab">CC</span>
                <input
                  className="input"
                  value={edit.cc}
                  placeholder="The Account Executive"
                  onChange={(e) => setEdit({ ...edit, cc: e.target.value })}
                />
              </label>
              <label className="col g4">
                <span className="flab">Subject</span>
                <input
                  className="input"
                  value={edit.subject}
                  onChange={(e) => setEdit({ ...edit, subject: e.target.value })}
                />
              </label>
              <label className="col g4">
                <span className="flab">Body (changes here are for this email only)</span>
                <textarea
                  className="input"
                  rows={14}
                  style={{ height: 'auto' }}
                  value={edit.body}
                  onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                />
              </label>
              <div className="okbox">
                {draft.attachments.map((a) => (
                  <div key={a.id} className="frow">
                    <span className="file">
                      <FileIcon className="ico s14" />
                      {a.fileName}
                    </span>
                  </div>
                ))}
                {draft.attachmentNote && (
                  <span className="okline amber">
                    <AlertTriangleIcon className="ico s14" />
                    <span>{draft.attachmentNote}</span>
                  </span>
                )}
                {draft.attachments.length > 0 && (
                  <div className="row g8 wrap">
                    <button type="button" className="btn btn-sm" onClick={downloadAll}>
                      <DownloadIcon className="ico s14" />
                      Download attachments
                    </button>
                  </div>
                )}
              </div>
              <div className="row g8 wrap">
                <a
                  className="btn btn-primary"
                  href={href}
                  onClick={() => setOpened(true)}
                  aria-label="Open in Outlook"
                >
                  <MailIcon className="ico s14" />
                  Open in Outlook
                </a>
                <a
                  className="btn"
                  href={outlookWebHref(edit)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLinkIcon className="ico s14" />
                  Outlook on the web
                </a>
                <button type="button" className="btn" onClick={() => void copy('subject')}>
                  <CopyIcon className="ico s14" />
                  Copy subject
                </button>
                <button type="button" className="btn" onClick={() => void copy('body')}>
                  <CopyIcon className="ico s14" />
                  Copy body
                </button>
                {draft.canSend && (
                  <button
                    type="button"
                    className="btn ml-auto"
                    disabled={marking}
                    onClick={() => void markSent()}
                  >
                    <CheckIcon className="ico s14" />
                    {sent ? 'Mark sent again' : 'Mark as sent'}
                  </button>
                )}
              </div>
              {step && step.state === 'open' && sent && draft.canSend && onMarkDone && (
                <div className="row g8 endj">
                  <span className="t12 muted">Sent before this step opened?</span>
                  <button type="button" className="btn btn-sm" onClick={onMarkDone}>
                    Mark step done
                  </button>
                </div>
              )}
              {step && step.state === 'open' && onSkip && draft.canSend && (
                <div className="row g8 endj">
                  <span className="t12 muted">No email to the insured this time?</span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-haspopup="dialog"
                    onClick={() => setSkipping(true)}
                  >
                    Skip email
                  </button>
                </div>
              )}
              {step?.state === 'done' && undo && (
                <div className="row endj">
                  <button type="button" className="linkbtn" onClick={undo}>
                    Untick this step
                  </button>
                </div>
              )}
              {opened && (
                <span className="okline amber">
                  <AlertTriangleIcon className="ico s14" />
                  <span>{outlookFallback}</span>
                </span>
              )}
              {href.length > longMailto && <p className="m0 t12 muted">{longBodyLine}</p>}
              <p className="m0 t12 faint">Sent by the portal in Phase 2</p>
            </>
          )}
        </div>
      )}
      {skipping && onSkip && (
        <SkipEmailDialog
          renewal={renewal}
          onClose={() => setSkipping(false)}
          onSkip={async (reason) => {
            if (await onSkip(reason)) setSkipping(false);
          }}
        />
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </div>
  );
}

/** Skip email (S17 extras; kit Dialog): why there is no email this time, required. */
function SkipEmailDialog({
  renewal,
  onClose,
  onSkip,
}: {
  renewal: RenewalDetail;
  onClose: () => void;
  onSkip: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const text = reason.trim();
  return (
    <Dialog
      id="skip-t"
      title="Skip email"
      subline={[
        renewal.insured,
        renewal.carrier ?? 'Carrier not in AMS360',
        renewal.line,
        renewal.policyNumber,
      ].join(' | ')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary ml-auto"
            disabled={!text || busy}
            onClick={async () => {
              setBusy(true);
              await onSkip(text);
              setBusy(false);
            }}
          >
            Skip email
          </button>
        </>
      }
    >
      <div>
        <label className="flab" htmlFor="skip-reason">
          Why is no email sent? <span className="req">*</span>
        </label>
        <textarea
          id="skip-reason"
          className="input"
          placeholder="For example: the insured asked for no email, the AE calls them"
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="fnote">
          Step 3 is ticked as skipped. The reason shows on the step and in the Portal log.
        </p>
      </div>
    </Dialog>
  );
}
